import { Link, useRouterState }  from '@tanstack/react-router';
import {
  LayoutDashboard, Building2, FileText, Banknote,
  ShieldMinus, ShieldPlus, AlertCircle, AlertTriangle, Mail, Calendar,
  Settings, ChevronLeft, ChevronDown, ChevronRight, BookOpen,
  CalendarClock, CircleDollarSign, Calculator, Users, MessageSquareMore,
  Wallet, Receipt, Store, Crown, History, Landmark, ArrowLeft, ServerCog,
} from 'lucide-react';
import { useState, useEffect, useRef, useCallback } from 'react';

import { EcocontLogo } from '@/components/shared/ecocont-logo';
import { Button }      from '@/components/ui/button';
import { ScrollArea }  from '@/components/ui/scroll-area';
import { cn }          from '@/lib/utils';
import { useAppStore } from '@/store/app.store';

// URL del ERP general (ecocont_ui) — solo se usa para el link "Volver al ERP",
// visible únicamente si esta sesión vino de ahí (ssoOrigin) Y la variable está
// seteada. Sin fallback hardcodeado a propósito — hay usuarios con acceso a
// este sistema pero sin acceso al ERP general, y un default adivinado podría
// apuntar a la URL equivocada en un entorno donde no corresponda mostrarlo.
const ECOCONT_UI_URL = import.meta.env.VITE_ECOCONT_UI_URL as string | undefined;

type TenantRole = 'OWNER' | 'ADMIN' | 'MEMBER' | 'VIEWER';

interface NavItem {
  path?:        string;               // omitido en un nodo que es solo subgrupo (sin destino propio)
  label:        string;
  icon:         React.ComponentType<{ className?: string }>;
  masterOnly?:  boolean;
  tenantRoles?: TenantRole[];
  /** Si tiene hijos, se renderiza como subgrupo colapsable en vez de link — anidable a cualquier profundidad. */
  children?:    NavItem[];
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Principal',
    items: [
      { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { path: '/empresas',  label: 'Empresas',  icon: Building2 },
    ],
  },
  {
    label: 'Estudio Contable',
    items: [
      { path: '/estudio',            label: 'Mi Estudio',       icon: Store },
      { path: '/paquetes',           label: 'Mi Suscripción',  icon: Crown,              tenantRoles: ['OWNER', 'ADMIN'] },
      { path: '/cobranzas',          label: 'Cobranzas',       icon: Wallet,             tenantRoles: ['OWNER', 'ADMIN'] },
      { path: '/comunicaciones',     label: 'Comunicaciones',  icon: History,            tenantRoles: ['OWNER', 'ADMIN'] },
      { path: '/whatsapp-templates', label: 'Plantillas WA',   icon: MessageSquareMore },
    ],
  },
  {
    label: 'Cumplimiento',
    items: [
      { path: '/sire',          label: 'SIRE',                  icon: BookOpen },
      { path: '/declaraciones', label: 'Declaraciones y Pagos', icon: FileText },
      { path: '/liquidacion',   label: 'Liquidación Tributaria', icon: Receipt },
      { path: '/afp-net',       label: 'Planillas AFP Net',     icon: Landmark },
    ],
  },
  {
    label: 'Recaudación',
    items: [
      { path: '/detracciones', label: 'Detracciones', icon: Banknote },
      { path: '/retenciones',  label: 'Retenciones',  icon: ShieldMinus },
      { path: '/percepciones', label: 'Percepciones', icon: ShieldPlus },
    ],
  },
  {
    label: 'Alertas',
    items: [
      { path: '/buzon',  label: 'Buzón SUNAT', icon: Mail },
      { path: '/deudas', label: 'Deudas',       icon: AlertCircle },
      { path: '/multas', label: 'Multas',       icon: AlertTriangle },
    ],
  },
  {
    label: 'Herramientas',
    items: [
      { path: '/calendario',       label: 'Calendario',      icon: Calendar },
      { path: '/cronograma-sunat', label: 'Cronograma SUNAT', icon: CalendarClock },
      { path: '/calculadora',      label: 'Calculadora',     icon: Calculator },
      { path: '/uit',              label: 'Tabla UIT',       icon: CircleDollarSign },
    ],
  },
  {
    label: 'Sistema',
    items: [
      { path: '/configuracion',  label: 'Configuración', icon: Settings },
      { path: '/usuarios',       label: 'Usuarios',      icon: Users, masterOnly: true },
      { path: '/sistema/worker', label: 'Colas y Cron',  icon: ServerCog, masterOnly: true },
    ],
  },
];

// ── Filtering (recursivo — un subgrupo sin hijos visibles desaparece) ────────

function filterItem(item: NavItem, isMaster: boolean, tenantRole: TenantRole, canManageCobranzas: boolean): NavItem | null {
  if (item.masterOnly && !isMaster) return null;
  if (item.tenantRoles && !isMaster) {
    // Cobranzas: un MEMBER ("Asistente") puntual puede tener acceso vía el
    // override canManageCobranzas de su ficha, sin necesitar el rol ADMIN.
    const cobranzaOverride = item.path === '/cobranzas' && tenantRole === 'MEMBER' && canManageCobranzas;
    if (!item.tenantRoles.includes(tenantRole) && !cobranzaOverride) return null;
  }
  if (!item.children) return item;
  const children = item.children
    .map(c => filterItem(c, isMaster, tenantRole, canManageCobranzas))
    .filter((c): c is NavItem => c !== null);
  if (children.length === 0) return null;
  return { ...item, children };
}

/** ¿Este item (o alguno de sus descendientes) es la ruta activa? */
function itemHasActive(item: NavItem, currentPath: string): boolean {
  if (item.path && currentPath.startsWith(item.path)) return true;
  return item.children?.some(c => itemHasActive(c, currentPath)) ?? false;
}

/** Junta las keys (path de labels) de todo subgrupo que contenga la ruta activa, para auto-abrirlos. */
function collectActiveKeys(items: NavItem[], currentPath: string, prefix: string, out: Set<string>): void {
  for (const item of items) {
    if (!item.children) continue;
    const key = `${prefix}/${item.label}`;
    if (itemHasActive(item, currentPath)) out.add(key);
    collectActiveKeys(item.children, currentPath, key, out);
  }
}

// ── Render recursivo de items (leaf o subgrupo anidado) ──────────────────────

function NavItemNode({
  item, depth, keyPrefix, currentPath, sidebarOpen, openKeys, toggleKey, onNavigate,
}: {
  item:        NavItem;
  depth:       number;
  keyPrefix:   string;
  currentPath: string;
  sidebarOpen: boolean;
  openKeys:    Set<string>;
  toggleKey:   (key: string) => void;
  onNavigate:  () => void;
}) {
  const key = `${keyPrefix}/${item.label}`;

  // Subgrupo: en modo íconos (sidebar colapsado) no hay espacio para un árbol,
  // así que se "aplana" — sus hijos se muestran como si fueran items sueltos.
  if (item.children) {
    if (!sidebarOpen) {
      return (
        <>
          {item.children.map(child => (
            <NavItemNode
              key={`${key}/${child.label}`}
              item={child} depth={depth} keyPrefix={key} currentPath={currentPath}
              sidebarOpen={sidebarOpen} openKeys={openKeys} toggleKey={toggleKey} onNavigate={onNavigate}
            />
          ))}
        </>
      );
    }

    const isOpen  = openKeys.has(key);
    const active  = itemHasActive(item, currentPath);
    const Icon    = item.icon;

    return (
      <div>
        <button
          onClick={() => { toggleKey(key); }}
          className={cn(
            'w-full flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
            depth > 0 && 'pl-7',
            active
              ? 'text-sidebar-primary font-semibold'
              : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground font-normal',
          )}
        >
          <Icon className={cn('h-[17px] w-[17px] shrink-0', active ? 'text-sidebar-primary' : 'text-sidebar-foreground/75')} />
          <span className="flex-1 text-left leading-snug truncate">{item.label}</span>
          {isOpen ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
        </button>
        {isOpen && (
          <div className="mt-0.5 flex flex-col gap-0.5">
            {item.children.map(child => (
              <NavItemNode
                key={`${key}/${child.label}`}
                item={child} depth={depth + 1} keyPrefix={key} currentPath={currentPath}
                sidebarOpen={sidebarOpen} openKeys={openKeys} toggleKey={toggleKey} onNavigate={onNavigate}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Leaf
  const active = !!item.path && currentPath.startsWith(item.path);
  const Icon   = item.icon;
  return (
    <Link
      to={item.path as '/'}
      onClick={onNavigate}
      data-active={active ? 'true' : undefined}
      className={cn(
        'relative flex items-center gap-3 rounded-lg transition-all duration-150',
        sidebarOpen
          ? cn('px-3 py-2 text-sm', depth > 0 && 'pl-7')
          : 'justify-center py-3 px-0',
        active
          ? 'bg-sidebar-primary/15 text-sidebar-primary font-semibold'
          : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground font-normal',
      )}
      title={!sidebarOpen ? item.label : undefined}
    >
      {active && sidebarOpen && (
        <span className="absolute left-0 top-[6px] bottom-[6px] w-[3px] rounded-full bg-sidebar-primary" />
      )}
      <Icon
        className={cn(
          'shrink-0 transition-colors',
          sidebarOpen ? 'h-[17px] w-[17px]' : 'h-5 w-5',
          active ? 'text-sidebar-primary' : 'text-sidebar-foreground/75',
        )}
      />
      {sidebarOpen && <span className="flex-1 leading-snug truncate">{item.label}</span>}
    </Link>
  );
}

function NavGroupSection({
  group, currentPath, isMaster, tenantRole, canManageCobranzas, sidebarOpen, toggleSidebar, isFirst, openKeys, toggleKey,
}: {
  group: NavGroup;
  currentPath: string;
  isMaster: boolean;
  tenantRole: TenantRole;
  canManageCobranzas: boolean;
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  isFirst: boolean;
  openKeys: Set<string>;
  toggleKey: (key: string) => void;
}) {
  const filteredItems = group.items
    .map(item => filterItem(item, isMaster, tenantRole, canManageCobranzas))
    .filter((item): item is NavItem => item !== null);

  if (filteredItems.length === 0) return null;

  const onNavigate = () => {
    if (typeof window !== 'undefined' && window.innerWidth < 1024) toggleSidebar();
  };

  return (
    <div className={cn('flex flex-col', !isFirst && 'mt-1')}>
      {/* Group header — etiqueta estática, siempre expandido (igual que aplicativos_v2:
          los grupos principales no colapsan, solo los subgrupos anidados dentro de ellos). */}
      {sidebarOpen ? (
        <p className={cn(
          'px-2 py-1.5 text-[11px] font-semibold tracking-widest uppercase text-sidebar-foreground/60 select-none',
          !isFirst && 'mt-2',
        )}>
          {group.label}
        </p>
      ) : (
        !isFirst && <div className="mx-auto my-2 h-px w-8 bg-sidebar-border/70 rounded-full" />
      )}

      {/* Items — siempre visibles */}
      <div className={cn('flex flex-col gap-0.5', sidebarOpen && 'mt-0.5')}>
        {filteredItems.map(item => (
          <NavItemNode
            key={`${group.label}/${item.label}`}
            item={item} depth={0} keyPrefix={group.label} currentPath={currentPath}
            sidebarOpen={sidebarOpen} openKeys={openKeys} toggleKey={toggleKey} onNavigate={onNavigate}
          />
        ))}
      </div>
    </div>
  );
}

export function Sidebar() {
  const { sidebarOpen, toggleSidebar, user, tenants, activeTenantId, ssoOrigin } = useAppStore();
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const isMaster    = user?.systemRole === 'MASTER';
  const tenantRole  = (tenants.find(t => t.id === activeTenantId)?.role ?? 'VIEWER') as TenantRole;
  const canManageCobranzas = tenants.find(t => t.id === activeTenantId)?.canManageCobranzas ?? false;

  // Keys de subgrupos anidados abiertos (independiente del open/close de cada NavGroup de primer nivel)
  const computeActiveKeys = useCallback(() => {
    const out = new Set<string>();
    for (const group of NAV_GROUPS) collectActiveKeys(group.items, currentPath, group.label, out);
    return out;
  }, [currentPath]);
  const [openKeys, setOpenKeys] = useState<Set<string>>(() => computeActiveKeys());
  useEffect(() => { setOpenKeys(computeActiveKeys()); }, [computeActiveKeys]);

  const toggleKey = (key: string) =>
    { setOpenKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    }); };

  // Lleva el link activo a la vista al cambiar de ruta (útil con subgrupos largos)
  const navRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const timer = setTimeout(() => {
      const active = nav.querySelector<HTMLAnchorElement>('[data-active="true"]');
      if (active) active.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 80);
    return () => { clearTimeout(timer); };
  }, [currentPath]);

  return (
    <aside
      className={cn(
        'hidden lg:flex lg:flex-col flex-col bg-sidebar border-r border-sidebar-border transition-all duration-200 safe-top safe-bottom',
        'relative z-auto',
        sidebarOpen ? 'w-[240px]' : 'w-[60px]',
      )}
    >
        {/* Logo / header */}
        <div className="flex h-[56px] items-center gap-2 px-3 border-b border-sidebar-border shrink-0">
          {sidebarOpen ? (
            <>
              <EcocontLogo
                variant="horizontal"
                className="h-10 w-auto max-w-[160px] object-left flex-1 min-w-0"
              />
              <Button
                variant="ghost"
                size="icon"
                className="shrink-0 h-7 w-7 text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent"
                onClick={toggleSidebar}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <button
              onClick={toggleSidebar}
              className="mx-auto flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 hover:bg-primary/20 transition-colors"
            >
              <EcocontLogo variant="isotipo" theme="light" className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Volver al ERP — solo si esta sesión vino del redirect SSO de ecocont_ui */}
        {ssoOrigin && ECOCONT_UI_URL && (
          <a
            href={ECOCONT_UI_URL}
            title={!sidebarOpen ? 'Volver al ERP' : undefined}
            className={cn(
              'flex items-center gap-2 text-sm text-sidebar-foreground border-b border-sidebar-border transition-colors',
              'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
              sidebarOpen ? 'px-3 py-2.5' : 'justify-center py-2.5',
            )}
          >
            <ArrowLeft className="h-[15px] w-[15px] shrink-0" />
            {sidebarOpen && 'Volver al ERP'}
          </a>
        )}

        {/* Navigation */}
        <ScrollArea className="flex-1 py-3">
          <nav ref={navRef} className="flex flex-col px-2 pb-4">
            {NAV_GROUPS.map((group, idx) => (
              <NavGroupSection
                key={group.label}
                group={group}
                currentPath={currentPath}
                isMaster={isMaster}
                tenantRole={tenantRole}
                canManageCobranzas={canManageCobranzas}
                sidebarOpen={sidebarOpen}
                toggleSidebar={toggleSidebar}
                isFirst={idx === 0}
                openKeys={openKeys}
                toggleKey={toggleKey}
              />
            ))}
          </nav>
        </ScrollArea>
      </aside>
  );
}
